/* eslint-disable */
import { getEarlyAchievableOffset } from './getEarlyAchievableOffset';
import { getAdjustedDate } from './getAdjustedDate';
import type { Allergies, UpdateUserRequest } from '../types/api';

type Params = {
  /** Email to attach (entered by the user, or taken from Apple Pay). */
  email: string;
  /** The onboarding zustand store snapshot (`useStore.getState()`). */
  store: any;
  /** routeGroups from remote-config (used for the target-weight-date logic). */
  flow: any;
  /** remote-config `sendEthnicity` value. */
  sendEthnicity?: string;
  /**
   * Active payment system. On TailoredPay the server switches access on by the
   * fact of payment, so `subscription.isActive` must NOT be sent — sending it
   * would have the client granting access, which is the Stripe-flow behaviour.
   */
  paymentSystem?: string;
};

/**
 * Builds the full post-purchase `update-by-token` payload (profile + subscription
 * + email). Shared between the paywall `onSuccess` (Apple Pay / non-deferred) and
 * the `email-after-payment` page (card, deferred-email) so both send identical data.
 */
export function buildPurchaseUserData({
  email,
  store,
  flow,
  sendEthnicity,
  paymentSystem,
}: Params): UpdateUserRequest {
  const serverGrantsAccess = paymentSystem === 'tailoredpay';
  const {
    born: age,
    gender,
    preferences: dietary,
    height,
    weight,
    idealWeight,
    when,
    exclude,
    goForWalks,
    name: userName,
    breakfast,
    snacks,
    imageUrl,
    pageAnswers,
  } = store;

  const ethnicityAnswers = Object.entries(pageAnswers || {}).map(([, value]: any) => {
    const questionText =
      (value?.title && value.title.en) ||
      (typeof value?.title === 'string' ? value.title : '');

    return {
      question: questionText,
      answer: Array.isArray(value?.answer) ? value.answer.join(', ') : value?.answer,
    };
  });

  const offset = getEarlyAchievableOffset(when);
  const adjustedDate = getAdjustedDate(when, offset);
  const showNewTargetDate = offset !== null && (flow?.flat?.() || []).includes('goal-2');

  return {
    email,
    ...(serverGrantsAccess
      ? {}
      : {
        subscription: {
          isActive: true,
        },
      }),
    ...(age && { age }),
    gender,
    activity_level: goForWalks || 'Active',
    dietary: dietary || 'No any preferences',
    allergies:
      exclude && exclude.length > 0
        ? (Object.fromEntries(
            ['no_allergies', 'gluten', 'wheat', 'lactose', 'nuts', 'eggs', 'fish', 'meat'].map(
              (allergy) => [allergy, exclude.includes(allergy)]
            )
          ) as Allergies)
        : {
            no_allergies: true,
            gluten: false,
            wheat: false,
            lactose: false,
            nuts: false,
            eggs: false,
            fish: false,
            meat: false,
          },
    height:
      height.type === 'ft'
        ? {
            ft: height.ft,
            type: 'ft',
          }
        : {
            cm: Number(height.cm),
            type: 'cm',
          },
    weight: {
      kg: weight.type === 'kg' ? Number(weight.kg) : undefined,
      lbs: weight.type === 'lbs' ? Number(weight.lbs) : undefined,
      type: weight.type,
    },
    targetWeight: idealWeight.value
      ? {
          kg: idealWeight.type === 'kg' ? Number(idealWeight.value) : undefined,
          lbs: idealWeight.type === 'lbs' ? Number(idealWeight.value) : undefined,
          type: idealWeight.type,
        }
      : {
          kg: weight.type === 'kg' ? Number(weight.kg) : undefined,
          lbs: weight.type === 'lbs' ? Number(weight.lbs) : undefined,
          type: weight.type,
        },
    targetWeightDate: (() => {
      if (showNewTargetDate) {
        return new Date(adjustedDate.year, +adjustedDate.month);
      }

      const flowPages = flow?.flat?.() || [];
      const hasWhenPage = flowPages.some((page: string) => page.toLowerCase().includes('when'));

      if (!hasWhenPage) {
        const oneYearLater = new Date();
        oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
        return oneYearLater;
      }

      const whenDate = new Date(+when.year, +when.month - 1, +when.day);

      return whenDate;
    })(),
    name: userName,
    preferredDailyMeals: {
      breakfast: breakfast === false ? false : true,
      snack: snacks === false ? false : true,
      lunch: true,
      dinner: true,
    },
    ...(() => {
      if (
        (sendEthnicity === 'onPersonalPlan' || sendEthnicity === 'onEmailAfterPayment') &&
        ethnicityAnswers.length > 0
      ) {
        return {
          ethnicity: {
            data: ethnicityAnswers,
            ...(imageUrl && { photoUrl: imageUrl }),
          },
        };
      }
      return { ethnicity: undefined };
    })(),
    responseWithAuthTokens: true,
  } as UpdateUserRequest;
}
